/**
 * Guards against re-fetching lookup data the prefetch already loaded.
 *
 * `usePrefetchStaticData` pulls ~27 lookup contexts once after login, and it
 * checks `hasContextData` before each so it never fetches twice. Screens then
 * asked for the same data again on mount — an unguarded `context.get({})` in a
 * `useEffect` — which is why Find Documents issued a second wave of requests
 * (destinations, users, travelAgencyAccounts) seconds after the first had
 * already returned them.
 *
 * Guarding rather than deleting those calls on purpose: a component can render
 * before the prefetch finishes (a deep link, a slow first load, a context that
 * is not in the prefetch list at all), and it still needs its own fetch then.
 * The guard makes the call a no-op only when the data is genuinely present.
 */

/**
 * Do we already hold this context's data?
 *
 * Providers expose their data in three different shapes depending on which
 * generation of the API hooks they were built with — a plain array, the
 * `{ ids, byId }` ByIdStructure, or a `dataAsArray` view. All three are checked
 * because the lookup contexts are a mix of all three.
 */
export function hasContextData(context: any): boolean {
    return !!(context?.data?.length || context?.data?.ids?.length || context?.dataAsArray?.length);
}

/**
 * Fetch a context's data only if it is not already loaded.
 *
 * Drop-in for a bare `someContext.get({})` inside a mount effect.
 */
export function ensureContextData(context: any, params: any = {}): void {
    if (hasContextData(context)) return;
    context?.get?.(params);
}
